import { AppInventoryOption, type AppInventoryState } from '@openx/types/targeting/appInventory';
import type { AppBundleIdOptionPayload } from '@openx/types/targeting/inventoryAndContent';
import type { TargetingAppInventory } from '@openx/types/targeting/targetingAppInventoryTypes';
import { Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import {
  comparisonMapToList,
  mapByComparison,
  transformAppBundleIdFromApiToState,
  transformAppBundleIdFromStateToApi,
  transformAppGroupFromApiToState,
  transformAppGroupFromStateToApi,
} from '../InventoryAndContent/utils';
import { getEmptyInventoryState } from './inventoryHelpers';
import { resolveAppInventoryOperator } from './utils/resolveAppInventoryOperator';

export const appInventoryApiToState = (
  inventoryApiData: TargetingAppInventory | null | undefined,
  appGroupsMap?: Map<string, string>
): AppInventoryState => {
  const initState: AppInventoryState = getEmptyInventoryState();

  if (!inventoryApiData) {
    return initState;
  }

  Object.keys(inventoryApiData).forEach(contentOption => {
    const data = inventoryApiData[contentOption];

    if (!data) {
      return;
    }

    switch (contentOption) {
      case AppInventoryOption.APP_BUNDLE_ID: {
        if (data?.val?.length || data?.inventorylist) {
          initState[contentOption] = transformAppBundleIdFromApiToState(data);
        }
        break;
      }
      case AppInventoryOption.APP_GROUP: {
        const appGroupData = data as { op: string; val: string } | undefined;

        if (appGroupData?.val) {
          const { comparisonType, enrichedValues } = transformAppGroupFromApiToState(appGroupData, appGroupsMap);

          initState[contentOption] = {
            op: appGroupData.op === Intersect.INTERSECTS ? Criteria.ANY : Criteria.ALL,
            val: { [comparisonType]: enrichedValues },
          };
        }
        break;
      }
      default: {
        if (data.op && Array.isArray(data.val)) {
          initState[contentOption] = {
            op: data.op,
            val: mapByComparison(data.val),
          };
        }
        break;
      }
    }
  });

  return initState;
};

export const appInventoryStateToApi = (inventoryState: AppInventoryState): TargetingAppInventory | null => {
  const apiData: TargetingAppInventory = {};

  Object.keys(inventoryState).forEach(contentOption => {
    const data = inventoryState[contentOption];

    switch (contentOption) {
      case AppInventoryOption.APP_BUNDLE_ID: {
        const appBundleIdState = data as AppBundleIdOptionPayload;
        const transformedData = transformAppBundleIdFromStateToApi(appBundleIdState);

        if (transformedData) {
          apiData[contentOption] = transformedData;
        }
        break;
      }

      case AppInventoryOption.APP_GROUP: {
        const transformedData = transformAppGroupFromStateToApi(data);

        if (transformedData) {
          apiData[contentOption] = transformedData;
        }
        break;
      }

      default: {
        const itemsList = comparisonMapToList(data.val);
        // only add content option if it contains any items
        if (itemsList.length) {
          apiData[contentOption] = {
            op: data.op,
            val: itemsList,
          };
        }
        break;
      }
    }
  });

  if (Object.keys(apiData).length === 0) {
    return null;
  }

  // The API requires this operator whenever App Name and App Bundle ID are combined, and rejects
  // the payload when it disagrees with their allow/block modes. It is never derived server side,
  // so it has to be recomputed on every write.
  if (apiData[AppInventoryOption.APP_GROUP] && apiData[AppInventoryOption.APP_BUNDLE_ID]) {
    apiData[AppInventoryOption.APP_INVENTORY_INTER_DIMENSION_OPERATOR] = resolveAppInventoryOperator(inventoryState);
  }

  return apiData;
};
